import Vue from 'vue'
import Vuex from 'vuex'
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import parseJwt from '@/helpers/parseJwt'

Vue.use(Vuex)

/** Evita pedidos paralelos duplicados a /api/account/relations */
let fetchAccountRelationsInFlight = null

const state = {
  userToken: localStorage.getItem('userToken') || null,
  currency: {},
  accountRelations: {},
  accountRelationsLastFetch: null,
  accountDetails: {},
  payload: {},
  accountInformation: {},
  initalBoot: false,
  hasAppLibrary: false,
  detailsLoaded: false,
  accountDetailsLastFetch: null,
  synchronizedTruckVisibility: { notified: true },
  featureFlags: {
    useVehicleApps: true,
    useEssentials: true,
    useDataServices: true
  }
}

const getters = {
  isAuthenticated: state => state.userToken !== null,
  isBooting: state => state.initalBoot,
  featureFlags: state => state.featureFlags
}

const mutations = {
  setCurrency(state, n) {
    state.currency = n
  },
  setSynchronizedVisibilityTrucks(state, payload) {
    state.synchronizedTruckVisibility = payload
  },
  setAccountDetails(state, n) {
    state.accountDetails = n
    state.detailsLoaded = true
  },
  setAccountRelations(state, payload) {
    state.accountRelations = payload
    state.accountRelationsLastFetch = Date.now()
  },
  setPayload(state, payload) {
    state.payload = payload
  },
  setAccountInformation(state, accountInfo) {
    state.accountInformation = accountInfo
    state.accountDetailsLastFetch = Date.now()
  },
  setUserToken(state, token) {
    state.userToken = token
  },
  setBootingStatus(state, isBooting) {
    state.initalBoot = isBooting
  },
  setHasAppLibrary(state, hasAppLibrary) {
    state.hasAppLibrary = !!hasAppLibrary
  },
  setFeatureFlags(state, featureFlags) {
    state.featureFlags = {
      ...featureFlags
    }
  }
}

const actions = {
  async getCurrencyInfo({ commit }) {
    return await apiCalls.get(api.Currencies, '', '').then(res => {
      const tt = res.data.filter(
        item => item.code === state.accountInformation.currency
      )
      commit('setCurrency', tt[0])
    })
  },
  async getAccountDetails({ commit, state }, forceRefresh = false) {
    // Cache de 60 segundos - dados da conta mudam menos frequentemente
    const CACHE_DURATION = 60000
    const now = Date.now()

    if (
      !forceRefresh &&
      state.detailsLoaded &&
      state.accountDetailsLastFetch &&
      now - state.accountDetailsLastFetch < CACHE_DURATION &&
      state.accountInformation &&
      Object.keys(state.accountInformation).length > 0
    ) {
      return Promise.resolve()
    }

    return await apiCalls.get(api.AccountDetails, '', '').then(res => {
      commit('setAccountDetails', res.data.CompanyInformation)
      // commit('setCurrency', { code: res.data.currency })
      commit('setAccountInformation', res.data)

      commit('setHasAppLibrary', res.data?.hasAppLibrary)

      commit('setFeatureFlags', res.data?.featureFlags)
    })
  },
  async synchronizeVisibilityTrucks({ commit }) {
    // Fire and forget - não aguarda a resposta da API
    apiCalls.post(api.SynchronizeVisibilityTrucks, '', '', '').catch(err => {
      console.error('Error synchronizing visibility trucks:', err)
    })

    // Retorna imediatamente com a resposta padrão
    const response = {
      addedVisibility: true,
      revokedVisibility: true
    }
    commit('setSynchronizedVisibilityTrucks', response)
    return response
  },
  async getAccountRelations({ commit, state }, forceRefresh = false) {
    const CACHE_DURATION = 60000
    const now = Date.now()

    if (
      !forceRefresh &&
      state.accountRelationsLastFetch != null &&
      now - state.accountRelationsLastFetch < CACHE_DURATION
    ) {
      return Promise.resolve()
    }

    if (!forceRefresh && fetchAccountRelationsInFlight) {
      return fetchAccountRelationsInFlight
    }

    const run = async () => {
      const res = await apiCalls.get(api.AccountRelations, '', '')
      commit('setAccountRelations', res.data)
    }

    fetchAccountRelationsInFlight = run().finally(() => {
      fetchAccountRelationsInFlight = null
    })

    return fetchAccountRelationsInFlight
  },
  async getPayload({ commit }, token) {
    const payload = parseJwt(token)
    commit('setPayload', payload)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
